import { AgGridTable } from '@components/AgGrid/AgGridTable';
import { ButtonAlert } from '@components/ButtonAlert';
import { ConfirmationDialog } from '@components/ConfirmationDialog';
import { TotalCount } from '@components/TotalCount';
import { PriceListTableCount } from '@components/TotalCount/PriceListTableCount';
import { RequestStatus } from '@features/request/requestSlice';
import type { CatalogUerOtherCostTemplateData } from '@gql/graphql';
import { ProjectMethodologyEnum } from '@gql/graphql';
import { useAccess } from '@hooks/useAccess';
import { useMethodsConfirmationDialog } from '@hooks/useMethodsConfirmationDialog';
import { Button, cnMixSpace, Grid, GridItem } from '@uikit';
import { destroyAgGridTableFilters, getCheckedIdsArr } from '@utils';

import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useCatalogListData } from '../CatalogList/hooks';
import { CatalogTablePage } from '../Common/CatalogTablePage';

import { AddOrEditOtherCostTemplatesModal } from './AddOrEditOtherCostTemplatesModal/AddOrEditOtherCostTemplatesModal';
import { CatalogOtherCostTemplatesToolbar } from './CatalogUerOtherCostTemplatesToolbar';
import { useCatalogOtherCostTemplatesColumns } from './hooks/useCatalogOtherCostTemplatesColumns';
import { useCatalogOtherCostTemplatesData } from './hooks/useCatalogOtherCostTemplatesData';
import { useRemoveOtherCostTemplates } from './hooks/useRemoveOtherCostTemplates';

import Styles from './catalogOtherCostTemplates.module.css';

const catalogPageTitle = 'Справочник шаблонов прочих затрат';

export const CatalogUerOtherCostTemplates = () => {
    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
    const checkedIdsArr = useMemo(() => getCheckedIdsArr(checkedIds), [checkedIds]);
    const [isCreating, setIsCreating] = useState(false);
    const [isCreateOrEditModalOpened, setIsCreateOrEditModalOpened] = useState(false);
    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
    const [openedTemplateId, setOpenedTemplateId] = useState<number | null>(null);

    const { catalogOtherCostTemplates: catalogOtherCostTemplatesRules } = useAccess();

    const gridRef = useRef<AgGridReact<CatalogUerOtherCostTemplateData> | null>(null);

    const { id } = useParams();
    const { tableData, fetchingStatus: catalogListStatus, getCatalogList } = useCatalogListData();

    const { updatedAt } = tableData.find((i) => i.id === id) || {};

    const refreshData = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.refreshInfiniteCache();
        }
    }, [gridRef]);

    const handleClearChecked = useCallback(() => {
        setCheckedIds({});
    }, []);

    const createOrEditModalHandler = useCallback((nextState) => {
        setIsCreateOrEditModalOpened(nextState);
        setIsCreating(false);
        setOpenedTemplateId(null);
    }, []);

    const handleCreateOtherCostTemplate = useCallback(() => {
        setIsCreateOrEditModalOpened(true);
        setIsCreating(true);
    }, []);

    const onRemoveSuccess = useCallback(() => {
        refreshData();
        getCatalogList();
        setCheckedIds({});
        destroyAgGridTableFilters({
            api: gridRef.current?.api,
            columns: ['name'],
        });
        setFilterValues({});
    }, [getCatalogList, refreshData]);

    const { columns } = useCatalogOtherCostTemplatesColumns({
        checkedIds,
        handleSetCheckedIds: setCheckedIds,
        createOrEditModalHandler,
        setOpenedTemplateId,
        onRemoveSuccess,
        setFilterValues,
        catalogOtherCostTemplatesRules,
    });

    const { datasource, totalCount, fetchedCount } = useCatalogOtherCostTemplatesData({
        filterValues,
        id,
    });

    const { catalogRemoveOtherCostTemplatesRequest } = useRemoveOtherCostTemplates(checkedIdsArr, onRemoveSuccess);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: () => catalogRemoveOtherCostTemplatesRequest(),
        });

    return (
        <CatalogTablePage
            catalogName={catalogPageTitle}
            methodology={[ProjectMethodologyEnum.Uer, ProjectMethodologyEnum.Bim]}
            isError={false}
            isLoading={false}
        >
            <CatalogOtherCostTemplatesToolbar
                handleCreateOtherCostTemplate={handleCreateOtherCostTemplate}
                updatedDate={updatedAt}
                catalogUpdatedDateInProgress={catalogListStatus === RequestStatus.FETCHING}
                catalogOtherCostTemplatesRules={catalogOtherCostTemplatesRules}
            />
            <div className={Styles.MainWrapper}>
                <Grid cols={2} className={Styles.WrapperCount}>
                    <GridItem>
                        {checkedIdsArr.length ? (
                            <div className={Styles.flexCenter}>
                                <TotalCount
                                    totalCount={checkedIdsArr.length}
                                    prefix={checkedIdsArr.length > 1 ? 'Выбрано' : 'Выбрана'}
                                    className={Styles.TotalCount}
                                />
                                <GridItem>
                                    <ButtonAlert size="xs" label="Удалить" onClick={handleOpenConfirmationDialog} />
                                    <Button
                                        label="Сбросить"
                                        view="ghost"
                                        size="xs"
                                        onClick={handleClearChecked}
                                        className={cnMixSpace({ mL: 's' })}
                                    />
                                </GridItem>
                            </div>
                        ) : (
                            <PriceListTableCount totalCount={fetchedCount} totalItems={totalCount} />
                        )}
                    </GridItem>
                </Grid>
                <div className={Styles.WrapperTable}>
                    <AgGridTable
                        className={Styles.OtherCostTemplateTable}
                        suppressCellFocus
                        rowModelType="infinite"
                        ref={gridRef}
                        columnDefs={columns}
                        maxConcurrentDatasourceRequests={1}
                        maxBlocksInCache={50}
                        cacheBlockSize={50}
                        datasource={datasource}
                    />
                </div>
            </div>
            {isCreateOrEditModalOpened && (
                <AddOrEditOtherCostTemplatesModal
                    isModalOpened={isCreateOrEditModalOpened}
                    closeHandler={createOrEditModalHandler}
                    isCreating={isCreating}
                    openedTemplateId={openedTemplateId}
                    refreshData={refreshData}
                    handlerOtherCostTemplatesUpdatedDate={getCatalogList}
                    gridRefApi={gridRef.current?.api}
                    setFilterValues={setFilterValues}
                />
            )}
            <ConfirmationDialog
                textHeader="Удаление шаблонов прочих затрат"
                textConfirmation="Вы уверены, что хотите удалить записи?"
                confirmButtonText="Удалить"
                cancelButtonText="Не удалять"
                onSubmit={handleActionQuery}
                isOpened={isOpenConfirmationDialog}
                onClose={handleCloseConfirmationDialog}
            />
        </CatalogTablePage>
    );
};

CatalogUerOtherCostTemplates.displayName = 'CatalogUerOtherCostTemplates';
